import { ScheduleBasedStatsDto } from "../../../../api-client";
import { useEffect, useState, useMemo } from 'react';
import useStore from "../../../../helpers/useStore";
import { BIContext } from "../../../../stores/BIDashboard/biDashboard.provider";
import { calculateScheduleBasedTotalRx } from "../../helpers/scheduleBasedStatsHelpers";
import { useDebounce } from "../../../Transactions/Insurance/Base/hooks/useDebounce";

interface TotalRxCountData {
  locationId: string, 
  locationName: string,
  date: Date, 
  totalCount: number
}

const useScheduleAllRxCountStats = (binType: 'day' | 'week' | 'month' | 'year', startDate: Date, endDate: Date, locationIds?: string[]) => {
  const BIStore = useStore(BIContext); 
  const { getScheduleBasedStats } = BIStore; 

  // Debounce date changes
  const debounceStartDate = useDebounce(startDate, 300);
  const debounceEndDate = useDebounce(endDate, 300);

  // Memoize locationIds to avoid unnecessary re-renders
  const memoizedLocationIds = useMemo(() => {
    if (!locationIds || locationIds.length === 0) {
      return undefined;
    }
    return locationIds;
  }, [locationIds?.join(','), locationIds?.length]);

  // Store all fetched stats (for "all locations")
  const [allScheduleBasedStats, setAllScheduleBasedStats] = useState<ScheduleBasedStatsDto[]>([]);
  // Store filtered stats per selected locations
  const [scheduleBasedStats, setScheduleBasedStats] = useState<ScheduleBasedStatsDto[]>([]);

  const [totalScheduleBasedRxCountResults, setTotalRxCountResults] = useState<TotalRxCountData[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!debounceStartDate || !debounceEndDate) {
        // console.log("[Schedule All Rx Count] Waiting for user to select both dates...");
        return;
      }
      
      try {
        setIsLoading(true);

        const response = await getScheduleBasedStats(debounceStartDate, debounceEndDate);
        // console.log("[Schedule All Rx Count] Backend responded with:", response);
        
        if (response && response.length > 0) {
          setAllScheduleBasedStats(response);
          
          // Apply location filter immediately when setting data
          if (memoizedLocationIds && memoizedLocationIds.length > 0) {
            setScheduleBasedStats(response.filter(p => memoizedLocationIds.includes(p.locationId)));
          } else {
            setScheduleBasedStats(response);
          }
        } else {
          // console.log("[Schedule All Rx Count] No data found for the specified range.");
          setAllScheduleBasedStats([]);
          setScheduleBasedStats([]);
        }
      } catch (error) {
        // console.log("[Schedule All Rx Count] Error fetching stats: ", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData(); 
  }, [debounceStartDate, debounceEndDate, getScheduleBasedStats]);

  // Filter locally whenever locations change (but not when data is initially loaded)
  useEffect(() => {
    if (!isLoading && allScheduleBasedStats.length > 0) {
      if (memoizedLocationIds && memoizedLocationIds.length > 0) {
        setScheduleBasedStats(allScheduleBasedStats.filter(p => memoizedLocationIds.includes(p.locationId)));
      } else {
        // No location selected → show all
        setScheduleBasedStats(allScheduleBasedStats);
      }
    }
  }, [memoizedLocationIds, allScheduleBasedStats, isLoading]);

  useEffect(() => {
    if (scheduleBasedStats.length > 0) {
      const totalRxCounts = calculateScheduleBasedTotalRx(scheduleBasedStats, binType);
      setTotalRxCountResults(totalRxCounts);
    } else {
      setTotalRxCountResults([]);
    }
  }, [binType, scheduleBasedStats]);

  const availableLocations = useMemo(() => {
    const locMap = new Map<string, string>();
    allScheduleBasedStats.forEach((p) => {
      if (p.locationId) locMap.set(p.locationId, p.locationName || p.locationId);
    });
    return Array.from(locMap.entries()).map(([id, name]) => ({ id, name }));
  }, [allScheduleBasedStats]);

  return { totalScheduleBasedRxCountResults, isLoading, availableLocations };
};

export default useScheduleAllRxCountStats;
